export const compactNumber = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 2,
});

export const integerNumber = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

export const decimalNumber = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

export const utcDateShort = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export const utcDateTimeShort = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

function truncateDecimals(value: number, digits: number): number {
  const factor = 10 ** digits;
  const scaled = value * factor;
  const truncatedScaled =
    value >= 0
      ? Math.floor(scaled + Number.EPSILON)
      : Math.ceil(scaled - Number.EPSILON);
  const truncated = truncatedScaled / factor;
  return Object.is(truncated, -0) ? 0 : truncated;
}

export function pricePrecision(value: number): 2 | 4 | 6 {
  const abs = Math.abs(value);
  if (abs >= 1 || abs === 0) return 2;
  if (abs >= 0.01) return 4;
  return 6;
}

export function formatPrice(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }

  const digits = pricePrecision(value);
  const truncated = truncateDecimals(value, digits);

  return truncated.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatCompact(value?: number | null, prefix = ""): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return `${prefix}${compactNumber.format(value)}`;
}

export function formatNumber(value?: number | null, digits = 0): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return value.toLocaleString("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

export function formatPercent(value?: number | null, digits = 2): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return `${value.toFixed(digits)}%`;
}

export function formatFunding(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return `${(value * 100).toFixed(4)}%`;
}

export function formatFundingBps(value?: number | null, digits = 2): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return `${(Math.abs(value) * 10_000).toFixed(digits)} bps`;
}

export function formatSpreadBps(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return "—";
  }
  return `${value.toFixed(2)} bps`;
}

export function formatUtcTick(value?: string): string {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) {
    return "—";
  }
  return utcDateTimeShort.format(date);
}
